import React from 'react';

import { ChevronRightIcon, RenderIconType } from '@adsk/alloy-react-icon';
import { StylableComponent } from '@adsk/nirvana-theme';
import Tooltip from '@adsk/nirvana-tooltip';

import { Wrapper, Label, Ellipsis } from './styles';
import { MenuOptionType } from '../types';

type RenderLabelProps = {
  children?: React.ReactNode;
};

type MenuItemProps = Omit<StylableComponent<HTMLLIElement>, 'onClick'> & {
  'data-testid'?: string;
  /** Label for the option */
  children?: React.ReactNode;
  /** show tooltip with higher priority than ellipsis text */
  tooltip?: React.ReactNode;
  /** Value for the option */
  value: string;
  /** Path for the option */
  path?: string;
  /** If the option is disabled */
  disabled?: boolean;
  /** If the option is active */
  isActive?: boolean;
  /** Render icon node */
  renderIcon?: RenderIconType;
  /** Render label node */
  renderLabel?: (props: RenderLabelProps) => React.ReactNode;
  /** Sub menu items */
  options?: MenuOptionType[];
  /** Callback on item click */
  onClick?: (value: string, path: string, options?: MenuOptionType[]) => void;
  /** Callback on item hover */
  onHover?: (value: string, path: string, options?: MenuOptionType[]) => void;
};

const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>(
  (
    {
      children,
      tooltip,
      value,
      disabled = false,
      isActive = false,
      path = '',
      options,
      renderIcon,
      renderLabel,
      onClick,
      onHover,
      ...rest
    },
    ref
  ) => {
    const handleClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        !disabled && onClick?.(value, path, options);
      },
      [disabled, value, path, options, onClick]
    );
    const handleHover = React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        !disabled && onHover?.(value, path, options);
      },
      [disabled, path, value, options, onHover]
    );

    return (
      <Wrapper
        ref={ref}
        {...rest}
        $disabled={disabled}
        $isActive={isActive}
        data-testid={rest['data-testid'] || `menu-item-${value}`}
        onClick={handleClick}
        onMouseOver={handleHover}
      >
        {renderIcon?.({ style: { marginLeft: '4px', flex: 'none' } })}
        {renderLabel && renderLabel({ children } as RenderLabelProps)}
        {!renderLabel &&
          (tooltip ? (
            <Tooltip content={tooltip}>
              <Ellipsis>{children}</Ellipsis>
            </Tooltip>
          ) : (
            <Label>{children}</Label>
          ))}
        {options && options.length > 0 && <ChevronRightIcon size={14} />}
      </Wrapper>
    );
  }
);

MenuItem.displayName = 'MenuItem';

export default Object.assign(MenuItem, {
  Label
});
